import React, { useState } from 'react';
import AccessibleModal from './AccessibleModal';
import { Mail, Phone, Shield, Activity, CheckCircle, Pause, Power, Copy } from 'lucide-react';
import type { Replica } from '../types/api';

interface EmployeeProfileProps {
    replica: Replica;
    onClose: () => void;
}

export default function EmployeeProfile({ replica, onClose }: EmployeeProfileProps) {
    const [activeTab, setActiveTab] = useState<'overview' | 'governance' | 'performance'>('overview');

    // Mock data for UI calculation
    const efficiency = 98;
    const tasksCompleted = 142;


    return (
        <AccessibleModal
            open={true}
            onClose={onClose}
            title={`${replica.name} employee profile`}
            backdropClassName="bg-black/60 backdrop-blur-sm !justify-end"
            panelClassName="w-full max-w-2xl bg-slate-900 h-full border-l border-slate-700 shadow-2xl flex flex-col overflow-hidden animate-slide-in"
        >

            {/* Header Profile */}
            <div className="relative h-48 bg-gradient-to-br from-indigo-900 to-slate-900">
                <button onClick={onClose} aria-label="Close profile" className="absolute top-4 left-4 p-2 bg-black/20 text-white rounded-full hover:bg-black/40 transition-colors">
                    ✕
                </button>
                <div className="absolute -bottom-12 left-8 flex items-end gap-4">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 p-1 shadow-xl">
                        <div className="w-full h-full bg-slate-800 rounded-xl flex items-center justify-center text-4xl font-bold text-white">
                            {replica.name.charAt(0)}
                        </div>
                    </div>
                    <div className="pb-4">
                        <h1 className="text-2xl font-bold text-white">{replica.name}</h1>
                        <p className="text-indigo-200">{replica.role || 'AI Employee'}</p>
                    </div>
                </div>
            </div>

            {/* Actions Bar */}
            <div className="mt-16 px-8 flex gap-3 pb-6 border-b border-slate-700">
                <button className="flex-1 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
                    <Mail size={18} /> Send Message
                </button>
                <button className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-600 flex items-center gap-2 transition-colors">
                    <Pause size={18} /> Pause
                </button>
                <button aria-label="Power off replica" className="px-4 py-2 bg-slate-800 hover:bg-red-900/30 text-red-400 hover:text-red-300 rounded-lg border border-slate-600 hover:border-red-900 flex items-center gap-2 transition-colors">
                    <Power size={18} />
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto">
                <div className="px-8 py-6">

                    {/* Tabs */}
                    <div className="flex gap-6 border-b border-slate-700 mb-6" role="tablist" aria-label="Employee profile sections">
                        {(['overview', 'governance', 'performance'] as const).map(tab => (
                            <button
                                key={tab}
                                role="tab"
                                aria-selected={activeTab === tab}
                                onClick={() => setActiveTab(tab)}
                                className={`pb-3 text-sm font-medium capitalize transition-colors border-b-2 ${activeTab === tab
                                    ? 'text-sky-400 border-sky-400'
                                    : 'text-slate-400 border-transparent hover:text-slate-200'
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Corporate ID Card */}
                            <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Contact Information</h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between group">
                                        <div className="flex items-center gap-3 text-slate-300">
                                            <Mail size={18} className="text-sky-500" />
                                            <span>{replica.name.toLowerCase().replace(' ', '.')}@company.com</span>
                                        </div>
                                        <button aria-label="Copy email address" className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-white transition-opacity"><Copy size={14} /></button>
                                    </div>
                                    <div className="flex items-center justify-between group">
                                        <div className="flex items-center gap-3 text-slate-300">
                                            <Phone size={18} className="text-green-500" />
                                            <span>+1 (555) 012-3456</span>
                                        </div>
                                        <button aria-label="Copy phone number" className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-white transition-opacity"><Copy size={14} /></button>
                                    </div>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                                    <div className="flex items-center gap-2 text-slate-400 mb-2">
                                        <CheckCircle size={16} /> Tasks
                                    </div>
                                    <p className="text-2xl font-bold text-white">{tasksCompleted}</p>
                                </div>
                                <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                                    <div className="flex items-center gap-2 text-slate-400 mb-2">
                                        <Activity size={16} /> Efficiency
                                    </div>
                                    <p className="text-2xl font-bold text-green-400">{efficiency}%</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'governance' && (
                        <div className="space-y-6">
                            <div className="bg-slate-800 p-5 rounded-xl border border-slate-700">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                                        <Shield size={20} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white">Active Contract</h3>
                                        <p className="text-xs text-slate-400">Locked on Dec 20, 2025</p>
                                    </div>
                                </div>
                                <div className="space-y-2 font-mono text-xs text-slate-400 bg-black/30 p-3 rounded lg">
                                    <p>Scope Hash: 0x8f...2a1b</p>
                                    <p>Permissions: [READ_CRM, WRITE_DRAFTS]</p>
                                    <p>Forbidden: [EXECUTE_PAYMENT]</p>
                                </div>
                            </div>

                            <div className="bg-slate-800 p-5 rounded-xl border border-slate-700">
                                <h3 className="text-sm font-semibold text-white mb-3">Decision Guardian</h3>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-xs font-bold text-white">
                                        ESS
                                    </div>
                                    <div>
                                        <p className="text-sm text-white">Essam (You)</p>
                                        <p className="text-xs text-slate-400">Level 1 Escalation Point</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'performance' && (
                        <div className="text-center py-12 text-slate-500">
                            <Activity size={48} className="mx-auto mb-4 opacity-50" />
                            <p>Performance metrics gathering...</p>
                        </div>
                    )}

                </div>
            </div>
        </AccessibleModal>
    );
}
